import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { format } from "date-fns";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,  
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputBase,
  LinearProgress,
  Rating,
  Tooltip,
  Typography,
} from "@mui/material";
import { Call, CheckCircle, Close, Email, SearchOutlined, SmartDisplay } from "@mui/icons-material";

import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import { IProducerAssignmentDialogProps } from "../types/crew-dashboard";
import { StreamResponse } from "../CrewDashboard";
import { RoleSkillTool } from "../types/add-crew-member";
import { PilotAPIResponse } from "../types/view-pilot";
import { TrashIcon } from "../../../components/icons";
import { AxiosError } from "axios";
import Paginator, { PaginationProps } from "../../../components/common/Paginator";
import { IRoleSkillTool } from "../types/rate-card";

interface AssignmentResponseProps {
  crew_member: {
    full_name: string;
  };
  id: number;
};

interface JobResponseProps {
  id: number;
};

interface CallsheetProps {
  id: number;
  assignment: Array<{
    role: string;
    notes: string | null;
  }>;
  crew_member: PilotAPIResponse;
  roles: IRoleSkillTool[];
};

export const ProducerAssignmentDialog: React.FC<IProducerAssignmentDialogProps> = ({
  setShowProducerAssignmentDialog,
  showProducerAssignmentDialog,
  selectedStream
}) => {
  const [producerOptions, setProducerOptions] = useState<PilotAPIResponse[]>([]);
  const [producerCount, setProducerCount] = useState<number>(0);
  const [selectedProducerRole, setSelectedProducerRole] = useState<RoleSkillTool | null>(null);
  const [stream, setStream] = useState<StreamResponse | null>(null);
  const [searching, setSearching] = useState<string>('');
  const [searched, setSearched] = useState<PilotAPIResponse[]>([]);
  const [currentAssignments, setCurrentAssignments] = useState<CallsheetProps[]>([]);
  const [producersLoading, setProducersLoading] = useState<boolean>(false);
  const [streamLoading, setStreamLoading] = useState<boolean>(false);
  const [assignmentLoading, setAssignmentLoading] = useState<boolean>(false);
  const [rolesLoading, setRolesLoading] = useState<boolean>(false);
  const [callsheetLoading, setCallsheetLoading] = useState<boolean>(false);
  const [producersError, setProducersError] = useState<string | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [callsheetError, setCallsheetError] = useState<string | null>(null);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const [producerRoles, setProducerRoles] = useState<RoleSkillTool[]>([]);
  const [producerRolesCount, setProducerRolesCount] = useState<number>(0);

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 0,
    pageSize: 5
  });

  const [prodPagination, setProdPagination] = useState<PaginationProps>({
    page: 0,
    pageSize: 10
  });

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const closeAndReset = () => {
    setShowProducerAssignmentDialog(false);
    setProducerOptions([]);
  };

  const fetchProducerRole = useCallback(async () => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      const role = await apiService.get<{ count: number, results: RoleSkillTool[] }>(
        `/api/crews/roles/`,
        { search: 'producer' }
      );

      setProducerRoles(role.data.results);
      setProducerRolesCount(role.data.count);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message ||
          "Failed to load producer roles. Please try again.";
        setRolesError(errorMessage);
      } else {
        console.log('Failed to load producer roles', error);
      }
    } finally {
      setRolesLoading(false);
    }
  }, []);

  const fetchCallsheet = useCallback(async () => {
    setCallsheetLoading(true);
    setCallsheetError(null);
    try {
      const callsheet = await apiService.get<{ results: CallsheetProps[] }>(
        `/api/call-sheets/callsheet/?stream=${selectedStream}`,
        { page_size: 25 }
      );
      
      setCurrentAssignments(callsheet.data.results);
    } catch (error) {
       if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to fetch callsheet. Try again.";
        setCallsheetError(errorMessage);
       } else {
        console.log('Failed to fetch callsheet', error);
       }
      setCurrentAssignments([]);
    } finally {
      setCallsheetLoading(false);
    }
  }, [selectedStream]);
  
  const fetchStream = useCallback(async () => {
    setStreamLoading(true);
    setStreamError(null);
    try {
      const strm = await apiService.get<StreamResponse>(
        `/api/streams/stream/${selectedStream}/`);
      
      setStream(strm.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to fetch stream. Try again.";
        setStreamError(errorMessage);
      } else {
        console.log('Failed to fetch stream', error);
      }
      setStream(null);
    } finally {
      setStreamLoading(false);
    }
  }, [selectedStream]);

  const fetchProducers = useCallback(async () => {
    setProducersLoading(true);
    setProducersError(null);
    try {
      const apiPage = prodPagination.page + 1;
      const producers = await apiService.get<{ count: number, results: PilotAPIResponse[] }>(
        `/api/crews/crew-members/?roles=${selectedProducerRole?.id}`,
        {
          page: apiPage,
          page_size: prodPagination.pageSize
        }
      );
      
      setProducerOptions(producers.data.results);
      setProducerCount(producers.data.count);
    } catch (error) {
       if (error instanceof AxiosError) {
        const errorMessage = 
          error.response?.data?.message ||
          "Failed to load producers. Please try again.";
        setProducersError(errorMessage);
       } else {
        console.error("Failed to fetch producers:", error);
       }
      setProducerOptions([]);
    } finally {
      setProducersLoading(false);
    }
  }, [prodPagination.page, prodPagination.pageSize, selectedProducerRole?.id]);

  useEffect(() => {
    if (producerOptions.length !== 0) {
      const filtering = producerOptions.filter(prod => {
        const searchedProducers = !searching ||
          prod.full_name.toLowerCase().includes(searching.toLowerCase());

        return searchedProducers;
      });

      setSearched(filtering);
    }
  }, [searching, producerOptions]);

  const handleProducerAssignment = async (user: number, producer: number) => {
    setAssignmentLoading(true);
    try {
      const payload = {
        stream_id: stream?.id,
        crew_member_id: producer,
        assignment: [{
          role: selectedProducerRole?.name,
          notes: ''
        }],
        roles_ids: [selectedProducerRole?.id]
      };

      const assignment = await apiService.post<AssignmentResponseProps>(
        `/api/call-sheets/callsheet/`, payload);

      const jobPayload = {
        name: stream?.title,
        currency: 'USD',
        stream: stream?.id,
        callsheet: assignment.data.id,
        assigned_to: user,
        amount: 0,
        description: stream?.title,
        status: 'pending',
        start_time: `${stream?.date}T${stream?.start}`,
        end_time: `${stream?.date}T${stream?.end}`
      };

      const job = await apiService.post<JobResponseProps>(
        `/api/jobs/pixel-pilot-jobs/`, jobPayload);

      const gigPayload = {
        job_id: job.data.id,
        crew_member_id: producer,
        status: 'pending'
      };

      await apiService.post(
        `/api/gigs/pixel-pilot-gigs/`, gigPayload);

      toast.success(
        `Successfully assigned ${assignment.data.crew_member.full_name} as Producer for the stream: ${stream?.title}`
      );
      fetchCallsheet();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to assign producer. Please try again.");
      } else {
        console.error("Failed to assign producer:", error);
      }
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleUnAssignment = async (
    id: number,
    producer: string,
    assignment: string
  ) => {
    setAssignmentLoading(true);
    try {
      await apiService.delete(`/api/call-sheets/callsheet/${id}/`);

      toast.success(
        `Successfully unassigned Pixel Pilot: ${producer} as ${assignment} from the stream: ${stream?.title}`
      );
      fetchCallsheet();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message || "Failed to unassign producer. Please try again.");
      } else {
        console.error("Failed to unassign producer:", error);
      }
    } finally {
      setAssignmentLoading(false);
    }
  };

  const retryStream = () => {
    fetchStream();
  };

  const retryProducers = () => {
    fetchProducers();
  };

  const retryRoles = () => {
    fetchProducerRole();
  };

  const retryCallsheet = () => {
    fetchCallsheet();
  };

  useEffect(() => {
    if (producerRoles) {
      setSelectedProducerRole(producerRoles[0]);
    }
  }, [producerRoles]);

  useEffect(() => {
    if (selectedStream) {
      fetchStream();
      fetchCallsheet();
    };
  }, [fetchCallsheet, fetchStream, selectedStream]);

  useEffect(() => {
    fetchProducerRole();
  }, [fetchProducerRole]);

  useEffect(() => {
    if (selectedProducerRole) {
      fetchProducers();
    };
  }, [fetchProducers, selectedProducerRole]);

  const checkAssignability = (producer: number) => {
    let currentAppearances = [];
    currentAppearances = currentAssignments?.filter(ca => ca.crew_member.id === producer);

    if (currentAppearances.some(ca => {
      return ca.roles[0]?.id === selectedProducerRole?.id;
    })) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <Dialog
      open={showProducerAssignmentDialog}
      onClose={closeAndReset}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Assign Producer
        <IconButton
          onClick={closeAndReset}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ my: 1 }}>
          {streamLoading ? (
            <Box display={'flex'} alignItems={'center'} gap={1}>
              <Chip
                label={
                  <Box display={'flex'} alignItems={'center'} gap={1}>
                    <CircularProgress color={"primary"} size={16} />
                    <i>Fetching Stream.</i>
                  </Box>
                }
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  borderColor: reduxTheme.primary_colors[800],
                }}
              />
            </Box>
          ) : streamError ? (
            <Alert severity="error" sx={{ alignItems: 'center' }}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                {streamError}
                <Button
                  onClick={() => retryStream()}
                  sx={{
                    color: reduxTheme.primary_colors[400],
                    backgroundColor: reduxTheme.primary_colors[900],
                  }}>
                  Retry
                </Button>
              </Box>
            </Alert>
          ) : !stream ? (
            <Alert severity="warning" sx={{ alignItems: 'center' }}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                {'Could not fetch stream.'}
                <Button
                  onClick={() => retryStream()}
                  sx={{
                    color: reduxTheme.primary_colors[400],
                    backgroundColor: reduxTheme.primary_colors[900],
                  }}>
                  Retry
                </Button>
              </Box>
            </Alert>
          ) : (
            <Box
              display={'flex'}
              sx={{
                alignItems: 'center',
                backgroundColor: reduxTheme.primary_colors[800],
                padding: 2,
                borderRadius: 2  
              }}>
              <Box display={'flex'} alignItems={'center'} gap={1}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 1,
                    backgroundColor: reduxTheme.primary_colors[400],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  {assignmentLoading ? (
                    <CircularProgress sx={{ color: '#FFF' }} size={24} />
                  ) : (
                    <SmartDisplay sx={{ color: '#FFF', fontSize: 32 }} />
                  )}
                </Box>
                <Box>
                  <Typography variant={'caption'}>Booking Confirmed</Typography>
                  <Typography variant={'subtitle1'} fontWeight={600}>{stream.title}</Typography>
                  <Typography variant={'caption'}>
                    {`${format(stream.date, 'dd MMM yyyy')} - ${stream.start}`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* ASSIGNMENTS */}
        <Grid container sx={{ my: 1 }} spacing={2}>
          <Grid size={5}>
            <Box
              sx={{
                alignItems: "center",
                mt: 1,
                backgroundColor: reduxTheme.primary_colors[800],
                padding: 2,
                borderRadius: 2,
              }}
            >
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Typography variant={"caption"}>Assigned:</Typography>
                <Typography variant={"caption"}>{`8/8`}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.floor((8 / 8) * 100)}
              />

              {rolesLoading ? (
                <Box display={"flex"} alignItems={"center"} gap={1}>
                  <Chip
                    label={
                      <Box display={"flex"} alignItems={"center"} gap={1}>
                        <CircularProgress color={"primary"} size={16} />
                        <i>Fetching Roles.</i>
                      </Box>
                    }
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 1.5,
                      borderColor: reduxTheme.primary_colors[800],
                    }}
                  />
                </Box>
              ) : rolesError ? (
                <Alert severity="error" sx={{ alignItems: "center" }}>
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    {rolesError}
                    <Button
                      onClick={() => retryProducers()}
                      sx={{
                        color: reduxTheme.primary_colors[400],
                        backgroundColor: reduxTheme.primary_colors[900],
                      }}
                    >
                      Retry
                    </Button>
                  </Box>
                </Alert>
              ) : !producerRoles ? (
                <Alert severity="warning" sx={{ alignItems: "center" }}>
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    {"Could not fetch roles."}
                    <Button
                      onClick={() => retryRoles()}
                      sx={{
                        color: reduxTheme.primary_colors[400],
                        backgroundColor: reduxTheme.primary_colors[900],
                      }}
                    >
                      Retry
                    </Button>
                  </Box>
                </Alert>
              ) : (
                <Box mt={2}>
                  <Typography variant={"caption"}>{`Roles`}</Typography>
                  {producerRoles.map((role) => {
                    return (
                      <Box
                        key={role.id}
                        display={"flex"}
                        onClick={() => setSelectedProducerRole(role)}
                        justifyContent={"space-between"}
                        sx={{
                          padding: 1,
                          // pointerEvents: role.name.toLowerCase().includes('producer') ? 'none' : 'auto',
                          cursor: "pointer",
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor:
                            selectedProducerRole?.id === role.id
                              ? reduxTheme.primary_colors[400]
                              : reduxTheme.primary_colors[900],
                          mb: 1,
                          borderRadius: 2,
                          backgroundColor: reduxTheme.primary_colors[900],
                        }}
                      >
                        <Box display={"flex"} alignItems={"center"} gap={1}>
                          <CheckCircle
                            sx={{ color: reduxTheme.primary_colors[400] }}
                          />
                          {/* {isComplete ? (
                              <CheckCircle sx={{ color: reduxTheme.primary_colors[400] }} />
                            ) : (
                              <RadioButtonUnchecked sx={{ color: reduxTheme.primary_colors[400] }} />
                            )} */}
                          <Typography variant={"caption"}>
                            {role.name}
                          </Typography>
                        </Box>
                        {/* <Typography variant={'caption'}>{`${role.completion}/${role.required}`}</Typography> */}
                      </Box>
                    );
                  })}
                  {pagination ? (
                    <Paginator
                      count={producerRolesCount}
                      pagination={pagination}
                      setPagination={setPagination}
                    />
                  ) : null}
                </Box>
              )}
            </Box>
          </Grid>
          <Grid size={7}>
            {/* SEARCH BAR */}
            <Box sx={{ my: 1 }}>
              <Box
                sx={{
                  backgroundColor: '',
                  border: '1px solid',
                  borderRadius: 2,
                  borderColor: reduxTheme.primary_colors[300],
                  display: "flex",
                  alignItems: "center",
                  py: .4,
                  pl: 1,
                  flex: 1,
                  minWidth: 320,
                }}>
                  <SearchOutlined sx={{ fontSize: 24, mr: 1, color: reduxTheme.primary_colors[300] }} />
                  <InputBase
                    placeholder="Search by crew member name..."
                    sx={{
                      padding: .4, flex: 1, color: '#757575'
                    }}
                    onChange={(e) => setSearching(e.target.value)}
                  />
              </Box>
            </Box>

            {(producersLoading && !selectedProducerRole) ? (
              <Box display={'flex'} alignItems={'center'} mb={1} gap={1}>
                <Chip
                  label={
                    <Box display={'flex'} alignItems={'center'} gap={1}>
                      <CircularProgress color={"primary"} size={16} />
                      <i>Fetching Producers.</i>
                    </Box>
                  }
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 1.5,
                    borderColor: reduxTheme.primary_colors[800],
                  }}
                />
              </Box>
            ) : (producersError && selectedProducerRole) ? (
              <Alert severity="error" sx={{ alignItems: 'center' }}>
                <Box display={'flex'} alignItems={'center'} gap={1}>
                  {producersError}
                  <Button
                    onClick={() => retryProducers()}
                    sx={{
                      color: reduxTheme.primary_colors[400],
                      backgroundColor: reduxTheme.primary_colors[900],
                    }}>
                    Retry
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {searched && searched.map(prod => {
                  return (
                    <Grid
                      size={4}
                      key={prod.id}
                      sx={{
                          alignItems: 'center',
                          backgroundColor: reduxTheme.primary_colors[800],
                          padding: 2,
                          borderRadius: 2
                      }}>
                      <Box display={'flex'} flexDirection={'column'} alignItems={'center'} gap={1}>
                          <Avatar src={prod.user.profile_picture ?
                            prod.user.profile_picture : `./placeholder.svg`}
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: 50
                            }}
                          />
                          <Box display={'flex'} alignItems={'center'} flexDirection={'column'}>
                            <Typography textAlign={'center'} variant={'subtitle2'}>{prod.full_name}</Typography>
                            <Rating size="small" value={prod.average_rating ? prod.average_rating : 0} precision={0.1} readOnly />
                          </Box>
                      </Box>
                      <Box display={'flex'} alignItems={'center'} flexDirection={'column'}>
                        <Chip
                          label={'Available'}
                          size={'small'}
                          sx={{
                            my: 1,
                            borderRadius: 20
                          }}
                        />
                        <Box sx={{ display: "flex", mb: .4, gap: 1 }}>
                          <Tooltip title={prod.user.phone_number || 'No phone number'}>
                            <IconButton
                              disabled={!prod.user.phone_number}
                              onClick={() =>
                                prod.user.phone_number &&
                                window.open(`tel:${prod.user.phone_number}`)
                              }
                              sx={{
                                color: prod.user.phone_number
                                  ? reduxTheme.primary_colors[400]
                                  : reduxTheme.primary_colors[800],
                                opacity: prod.user.phone_number ? 1 : 0.5,
                                "&:hover": {
                                  color: prod.user.phone_number
                                    ? reduxTheme.primary_colors[400]
                                    : reduxTheme.primary_colors[800],
                                },
                              }}
                            >
                              <Call fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={prod.user.email || 'No email address'}>
                            <IconButton
                              disabled={!prod.user.email}
                              onClick={() =>
                                prod.user.email &&
                                window.open(`mailto:${prod.user.email}`)
                              }
                              sx={{
                                color: prod.user.email
                                  ? reduxTheme.primary_colors[400]
                                  : reduxTheme.primary_colors[800],
                                opacity: prod.user.email ? 1 : 0.5,
                                "&:hover": {
                                  color: prod.user.email
                                    ? reduxTheme.primary_colors[400]
                                    : reduxTheme.primary_colors[800],
                                },
                              }}
                            >
                              <Email fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Button
                          fullWidth
                          disabled={assignmentLoading || !checkAssignability(prod.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProducerAssignment(prod.user.id, prod.id);
                          }}
                          variant={"contained"}>
                          {'Assign'}
                        </Button>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
            {pagination && (
              <Paginator
                count={producerCount}
                pagination={prodPagination}
                setPagination={setProdPagination}
              />
            )}
          </Grid>
        </Grid>

        {/* CURRENT ASSIGNMENTS */}
        <Box sx={{ mt: 1, pt: 1, borderWidth: 1, borderTopStyle: 'solid', borderColor: 'divider' }}>
          <Box display={'flex'} alignItems={'center'} mb={1} gap={1}>
            <Typography variant={'subtitle2'}>Current Assignments</Typography>
            {callsheetLoading && (
              <CircularProgress color={"primary"} size={16} />
            )}
          </Box>

          <Box sx={{
            width: '100%',
            backgroundColor: reduxTheme.primary_colors[800],
            mt: 1,
            padding: 1,
            borderRadius: 2
          }}>
            <Grid
              container
              spacing={2}
              sx={{
                mb: 1,
                bgcolor: reduxTheme.primary_colors[800],
                padding: .4,
                borderRadius: 1
              }}>
              <Grid size={3} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Assignment</Typography>
              </Grid>
              <Grid size={3} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Pilot Name</Typography>
              </Grid>
              <Grid size={3.5} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Phone</Typography>
              </Grid>
              <Grid size={1.5} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Email</Typography>
              </Grid>
              <Grid size={1} sx={{ textTransform: 'uppercase' }}>
                <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>Actions</Typography>
              </Grid>
            </Grid>
            {callsheetError && (
              <Alert severity="error" sx={{ alignItems: 'center' }}>
                <Box display={'flex'} alignItems={'center'} gap={1}>
                  {callsheetError}
                  <Button
                    onClick={() => retryCallsheet()}
                    sx={{
                      color: reduxTheme.primary_colors[400],
                      backgroundColor: reduxTheme.primary_colors[900],
                    }}>
                    Retry
                  </Button>
                </Box>
              </Alert>
            )}
            {currentAssignments?.length ? (
              currentAssignments.map((ca, index) => {
                const rowBg = index % 2 === 0 ? '#FFF' : 'transparent';
                return (
                  <Grid
                    container
                    key={ca.id}
                    display={'flex'}
                    alignItems={'center'}
                    spacing={2}
                    sx={{
                      mb: 1,
                      bgcolor: rowBg,
                      padding: 1,
                      borderRadius: 1
                    }}>
                    <Grid size={3}>
                      <Typography variant={'body2'}>{ca.roles[0]?.name}</Typography>
                    </Grid>
                    <Grid size={3}>
                      <Typography variant={'body2'}>{ca.crew_member.full_name}</Typography>
                    </Grid>
                    <Grid size={3.5}>
                      <Typography variant={'body2'}>{ca.crew_member.user.email}</Typography>
                    </Grid>
                    <Grid size={1.5}>
                      <Typography variant={'body2'}>{ca.crew_member.user.phone_number}</Typography>
                    </Grid>
                    <Grid size={1}>
                      <IconButton
                        disabled={assignmentLoading}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUnAssignment(
                            ca.id,
                            ca.crew_member.full_name,
                            ca.roles[0]?.name
                          );
                        }}>
                        <TrashIcon color="error" sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })
            ) : (
              <Alert severity="warning" sx={{ alignItems: 'center' }}>
                {`No Callsheet Has Been Created For The Stream: ${stream?.title} Yet.`}
              </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={closeAndReset}
          disabled={assignmentLoading}
          sx={{
            color: reduxTheme.primary_colors[100],
            backgroundColor: reduxTheme.primary_colors[800],
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProducerAssignmentDialog;
